import $ from 'jquery'
import memo from 'lodash/memoize'

export const createEvent = (name: string): Event => {
  return new Event(name)
}

export const escapeCssSelector = (id: string): string => {
  // Check if CSS.escape is available and use it if it is.
  if (typeof CSS !== 'undefined' && CSS.escape) {
    return CSS.escape(id)
  }

  // Fallback for environments where CSS.escape is not available.
  // This handles a subset of scenarios such as leading numbers and special characters.
  return id.replace(/([^\w-])/g, '\\$1').replace(/^(\d)/, '\\3$1 ')
}

export const InputEvent = (): Event => createEvent('input')

export const makeMeSticky = (selector: string, top: number): void => {
  $(selector).css({ position: 'sticky', top: `${top}px` })
}

export const isVisible = (element: HTMLElement): boolean => {
  if (!element) return false

  // This solution was used in jQuery:visible
  return !!(
    element.offsetWidth ||
    element.offsetHeight ||
    element.getClientRects().length
  )
}

export const getScrollbarWidth = (element: HTMLElement): boolean | number => {
  if (!element) {
    return false
  }

  return element.offsetWidth - element.clientWidth
}

export const rightDrawerSelector = '#right-drawer'
export const rightDrawerSidepaneContainerSelector = `${rightDrawerSelector} #sidepane-container`

export const animatedScroll = (
  element: HTMLElement,
  container = 'html',
  offset?: number
): Promise<void> => {
  return new Promise(function (resolve) {
    setTimeout(() => {
      if (element) {
        const baseOffset = offset || 80

        const containerScrollTop = $(container).scrollTop() as number
        const elementOffsetTop = $(element).offset()?.top || 0
        const windowScrollTop = $(window).scrollTop() || 0

        const scrollTop =
          containerScrollTop + elementOffsetTop - baseOffset - windowScrollTop

        $(container).animate({ scrollTop: scrollTop }, 300)
        resolve()
      }
    }, 50)
  })
}

export const _scrollTo = (
  elementSelector: string,
  containerSelector = 'html',
  offset?: number
): Promise<void> => {
  const element = $(elementSelector)[0]

  return animatedScroll(element, containerSelector, offset)
}

export const isScrolledIntoView = (
  element: HTMLElement,
  container: HTMLElement
): boolean => {
  if (!element || !container) return false

  const elementRect = element.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  return (
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom
  )
}

export const readCSSVariable = (variable: string): string => {
  return getComputedStyle(document.documentElement).getPropertyValue(variable)
}

export const readCSSVariables = (variables: string[]): Array<string> => {
  return variables.map(readCSSVariable)
}

export const readCSSVariableMemo = memo(readCSSVariable)

export const scrollToElement = (
  elementSelector: string,
  distance: ScrollLogicalPosition = 'center'
): void => {
  const element = document.querySelector(elementSelector)
  if (element) element.scrollIntoView({ behavior: 'smooth', block: distance })
}

export const scrollToElementWithOffset = (
  elementSelector: string,
  offset: number
): void => {
  const element = document.querySelector(elementSelector)

  if (element) {
    window.scrollTo({
      top: element.getBoundingClientRect().top + window.scrollY - offset,
      behavior: 'smooth',
    })
  }
}

export const scrollableContentSelector =
  '#main-content .main-content__scroll-container'

export const isElementAnchorInView = (
  element: HTMLElement,
  container: HTMLElement
): boolean => {
  const elementRect = element.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  return (
    elementRect.top >= containerRect.top &&
    elementRect.top <= containerRect.bottom &&
    elementRect.left >= containerRect.left &&
    elementRect.left <= containerRect.right
  )
}

export const waitForElement = (
  doc: Document,
  selector: string
): Promise<HTMLElement> => {
  return new Promise((resolve) => {
    const elm = doc.querySelector(selector) as HTMLElement

    if (elm) {
      return resolve(elm)
    }

    const observer = new MutationObserver(() => {
      const elm = doc.querySelector(selector) as HTMLElement

      if (elm) {
        resolve(elm)
        observer.disconnect()
      }
    })

    observer.observe(doc.body, {
      childList: true,
      subtree: true,
    })
  })
}

/**
 * Determine the direction in which an element is clipped by its container.
 * @param element - The element to check
 * @param container - The container to check against
 * @returns - An object with boolean values indicating if the element is clipped in each direction
 */
export const getClippedDirection = (
  element: HTMLElement,
  container: HTMLElement,
  offset: {
    top?: number
    bottom?: number
    left?: number
    right?: number
  } = {}
): {
  top: boolean
  bottom: boolean
  left: boolean
  right: boolean
} => {
  if (!element || !container) {
    return {
      top: false,
      bottom: false,
      left: false,
      right: false,
    }
  }

  const elementRect = element.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  const elementTop = elementRect.top
  const elementBottom = elementRect.bottom
  const elementLeft = elementRect.left
  const elementRight = elementRect.right

  const containerTop = containerRect.top
  const containerBottom = containerRect.bottom
  const containerLeft = containerRect.left
  const containerRight = containerRect.right

  const offsetTop = offset.top || 0
  const offsetBottom = offset.bottom || 0
  const offsetLeft = offset.left || 0
  const offsetRight = offset.right || 0

  return {
    top: elementTop + offsetTop < containerTop,
    bottom: elementBottom + offsetBottom > containerBottom,
    left: elementLeft + offsetLeft < containerLeft,
    right: elementRight + offsetRight > containerRight,
  }
}

/**
 * Runs a callback to modify the input element and then restores focus if it was previously focused.
 * @param modifyInputCallback
 * @param input
 */
export const modifyAndMaintainFocus = (
  modifyInputCallback: () => void,
  input: HTMLInputElement
): void => {
  const wasFocused: boolean = document.activeElement === input
  modifyInputCallback()
  if (wasFocused) {
    window.requestAnimationFrame(() => {
      input.focus()
    })
  }
}

/**
 * Get the navbar height, adds an offset in case the body does not have a no-body-padding class
 * The body top padding can change dynamically on page resizes.
 * @returns {<number>} - The integer height for the total navbar height
 */
export const getNavbarHeight = (): number => {
  const navbar = document.querySelector(
    '[data-role=navbar-height]'
  ) as HTMLElement

  const offset = document.body.classList.contains('no-body-padding') ? 0 : 20
  const navbarHeight = navbar ? navbar.offsetHeight : 0

  return navbarHeight + offset
}

export const getStickyHeaderHeight = (): string =>
  readCSSVariable('--sticky-header-height') || '0px'

export const getMainSubheaderHeight = (): string =>
  readCSSVariable('--main-subheader-height') || '0px'

export const getSecondarySubheaderHeight = (): string =>
  readCSSVariable('--secondary-subheader-height') || '0px'

export const getSystemBannerHeight = (): string =>
  readCSSVariable('--system-banner-height') || '0px'

export const getCommunicationSidepaneHeight = (): string =>
  readCSSVariable('--communication-sidepane-header-height') || '0px'

export const getRightSidebarScrollOffset = (): number =>
  [
    getStickyHeaderHeight(),
    getMainSubheaderHeight(),
    getCommunicationSidepaneHeight(),
  ].reduce((acc, height) => acc + parseInt((height || '').replace('px', '')), 8)

export * from './jquery-alternatives'

export const isUsingTwoDrawerLayout = (): boolean =>
  document.querySelector('.two-drawer-layout') !== null ||
  document.querySelector('.two-drawer-layout-new') !== null
